


import { Select, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { RosterItem } from './types';


const shiftOptions = [
  { label: 'WFH', value: 'WFH' },
  { label: 'Client Place', value: 'Client Place' },
  { label: 'Office', value: 'Office' },
  { label: 'Week off', value: 'Week off' },
];

export const getRosterColumns = (
  handleCellChange: (value: string, record: RosterItem, day: string) => void,
  handleEdit: (record: RosterItem) => void,
  handleSave: (record: RosterItem) => void,
  handleCancel: () => void,
  handleDelete: (ids: (string | number)[]) => void,
  editingKey: string | number,
  viewMode: 'plan' | 'add',
  originalRecord: RosterItem | null,
  isActionEnabled: boolean
): ColumnsType<RosterItem> => {
  const isEditing = (record: RosterItem) => record.key === editingKey;

  const renderCell = (text: string, record: RosterItem, day: string) => {
    if (isEditing(record)) {
      return (
        <Select
          value={text || undefined}
          onChange={(value) => handleCellChange(value, record, day)}
          bordered={false}
          className="roster-select"
          placeholder="Select"
          options={shiftOptions}
          style={{ width: '100%' }}
          popupMatchSelectWidth={true}
          popupClassName="roster-shift-dropdown"
        />
      );
    }
    return <span>{text || '-'}</span>;
  };

  return [
    {
      title: 'Employee Code',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      align: 'left' as const,
      className: 'employee-code-col',
      render: (text: string) => (
        <Tooltip title={text || '-'}>
          <span className="text-gray-500">{text || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Employee Name',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 160,
      ellipsis: true,
      align: 'left' as const,
      render: (_: unknown, record: RosterItem) => {
        const fullName = `${record.firstName} ${record.lastName}`;
        const isTruncated = fullName.length > 27;
        return (
          <Tooltip placement="top" title={isTruncated ? fullName : ''}>
            <span className="font-medium text-gray-500">
              {isTruncated ? `${fullName.substring(0, 27)}...` : fullName}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Mon',
      dataIndex: 'mon',
      key: 'mon',
      width: 110,
      align: 'center' as const,
      render: (text: string, record: RosterItem) => renderCell(text, record, 'mon')
    },
    {
      title: 'Tue',
      dataIndex: 'tue',
      key: 'tue',
      width: 110,
      align: 'center' as const,
      render: (text: string, record: RosterItem) => renderCell(text, record, 'tue')
    },
    {
      title: 'Wed',
      dataIndex: 'wed',
      key: 'wed',
      width: 110,
      align: 'center' as const,
      render: (text: string, record: RosterItem) => renderCell(text, record, 'wed')
    },
    {
      title: 'Thu',
      dataIndex: 'thu',
      key: 'thu',
      width: 110,
      align: 'center' as const,
      render: (text: string, record: RosterItem) => renderCell(text, record, 'thu')
    },
    {
      title: 'Fri',
      dataIndex: 'fri',
      key: 'fri',
      width: 110,
      align: 'center' as const,
      render: (text: string, record: RosterItem) => renderCell(text, record, 'fri')
    },
    {
      title: 'Sat',
      dataIndex: 'sat',
      key: 'sat',
      width: 110,
      align: 'center' as const,
      render: (text: string, record: RosterItem) => renderCell(text, record, 'sat')
    },
    {
      title: 'Sun',
      dataIndex: 'sun',
      key: 'sun',
      width: 110,
      align: 'center' as const,
      render: (text: string, record: RosterItem) => renderCell(text, record, 'sun')
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      align: 'center' as const,
      render: (_: unknown, record: RosterItem) => {
        const editable = isEditing(record);
        return editable ? (
          <div className='flex justify-center gap-2'>
            {(() => {
              const isAddDisabled = viewMode === 'add' && (!record.mon || !record.tue || !record.wed || !record.thu || !record.fri || !record.sat || !record.sun);

              const isPlanDisabled = viewMode === 'plan' && originalRecord &&
                record.mon === originalRecord.mon &&
                record.tue === originalRecord.tue &&
                record.wed === originalRecord.wed &&
                record.thu === originalRecord.thu &&
                record.fri === originalRecord.fri &&
                record.sat === originalRecord.sat &&
                record.sun === originalRecord.sun;

              const isDisabled = isAddDisabled || isPlanDisabled || !isActionEnabled;

              if (isDisabled) {
                return (
                  <span
                    className="material-symbols-outlined text-gray-400 cursor-not-allowed"
                  >
                    check
                  </span>
                );
              }

              return (
                <Tooltip placement="top" title="Save">
                  <span
                    className="material-symbols-outlined cursor-pointer text-green-600 hover:text-green-800"
                    onClick={() => handleSave(record)}
                  >
                    check
                  </span>
                </Tooltip>
              );
            })()}
            <Tooltip placement="top" title="Cancel">
              <span
                className="material-symbols-outlined cursor-pointer text-red-600 hover:text-red-800"
                onClick={handleCancel}
              >
                close
              </span>
            </Tooltip>
          </div>
        ) : (
          <div className='flex justify-center gap-2'>
            <Tooltip placement="top" title={viewMode === 'add' ? 'Add' : 'Edit'}>
              <span
                className={`material-symbols-outlined ${(editingKey !== '' || !isActionEnabled) ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer hover:text-gray-700'}`}
                onClick={() => (editingKey === '' && isActionEnabled) && handleEdit(record)}
              >
                {viewMode === 'add' ? 'add' : 'edit'}
              </span>
            </Tooltip>
            {viewMode === 'plan' && (
              <Tooltip placement="top" title="Delete">
                <span
                  className={`material-symbols-outlined ${(editingKey !== '' || !isActionEnabled) ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => (editingKey === '' && isActionEnabled) && handleDelete([record?.key as number])}
                >
                  delete
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    }
  ];
};